import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface ServiceDetail {
  id: string;
  name: string;
  description: string;
  applications: string[];
  benefits: string[];
  equipment: string[];
}

const Services: React.FC = () => {
  const [expandedService, setExpandedService] = useState<string | null>(null);

  const advancedServices: ServiceDetail[] = [
    {
      id: 'remote-field-ect',
      name: 'Remote Field ECT',
      description: 'Advanced electromagnetic testing technique for detecting corrosion and wall thinning in ferromagnetic tubes.',
      applications: [
        'Heat exchanger tube inspection',
        'Boiler tube assessment',
        'Condenser tube evaluation',
        'Pipeline integrity assessment'
      ],
      benefits: [
        'Detects both internal and external corrosion',
        'Works through insulation and coatings',
        'High sensitivity to wall thinning',
        'Non-contact inspection method'
      ],
      equipment: [
        'Remote Field ECT Probes',
        'Multi-frequency ECT Systems',
        'Data Acquisition Units',
        'Analysis Software'
      ]
    },
    {
      id: 'tofd',
      name: 'TOFD (Time of Flight Diffraction)',
      description: 'Ultrasonic testing method that provides high-resolution imaging of weld defects and material discontinuities.',
      applications: [
        'Weld inspection and characterization',
        'Pressure vessel assessment',
        'Pipeline girth weld testing',
        'Structural steel evaluation'
      ],
      benefits: [
        'High detection sensitivity',
        'Accurate defect sizing',
        'Real-time imaging capability',
        'Reduced inspection time'
      ],
      equipment: [
        'TOFD Probes and Wedges',
        'Multi-channel UT Systems',
        'Phased Array Controllers',
        'Imaging Software'
      ]
    },
    {
      id: 'phased-array',
      name: 'Phased Array Ultrasonics',
      description: 'Advanced ultrasonic testing using multiple elements to create focused beams and complex scan patterns.',
      applications: [
        'Weld inspection',
        'Corrosion mapping',
        'Thickness measurement',
        'Defect characterization'
      ],
      benefits: [
        'Multiple beam angles in single setup',
        'Real-time imaging',
        'Improved defect detection',
        'Reduced inspection time'
      ],
      equipment: [
        'Phased Array Probes',
        'Multi-channel UT Systems',
        'Encoder Systems',
        'Analysis Software'
      ]
    },
    {
      id: 'ultra-wave-lrt',
      name: 'Ultra Wave LRT',
      description: 'Long Range Ultrasonic Testing for detecting corrosion and defects in pipes over long distances.',
      applications: [
        'Pipeline integrity assessment',
        'Long-distance corrosion detection',
        'Pipe wall thickness monitoring',
        'Structural integrity evaluation'
      ],
      benefits: [
        'Long-range inspection capability',
        'Detects corrosion and defects',
        'Minimal surface preparation',
        'Cost-effective for long pipes'
      ],
      equipment: [
        'LRT Transducers',
        'Pulse Generators',
        'Data Acquisition Systems',
        'Analysis Software'
      ]
    },
    {
      id: 'tank-floor-scanning',
      name: 'Tank Floor Scanning',
      description: 'Comprehensive inspection of storage tank floors using advanced ultrasonic and electromagnetic methods.',
      applications: [
        'Storage tank floor inspection',
        'Corrosion mapping',
        'Thickness measurement',
        'Integrity assessment'
      ],
      benefits: [
        'Complete floor coverage',
        'High-resolution mapping',
        'Corrosion detection',
        'Compliance verification'
      ],
      equipment: [
        'Automated Scanning Systems',
        'Ultrasonic Probes',
        'Electromagnetic Sensors',
        'Data Processing Software'
      ]
    },
    {
      id: 'eddy-current',
      name: 'Eddy Current Testing',
      description: 'Electromagnetic testing method for detecting surface and near-surface defects in conductive materials.',
      applications: [
        'Surface crack detection',
        'Conductivity measurement',
        'Coating thickness measurement',
        'Material sorting'
      ],
      benefits: [
        'High sensitivity to surface defects',
        'Fast inspection speed',
        'No coupling medium required',
        'Real-time results'
      ],
      equipment: [
        'Eddy Current Probes',
        'Multi-frequency ECT Systems',
        'Impedance Analyzers',
        'Analysis Software'
      ]
    },
    {
      id: 'remote-visual',
      name: 'Remote Visual Inspection',
      description: 'Advanced visual inspection using cameras and robotic systems for hard-to-reach areas.',
      applications: [
        'Internal pipe inspection',
        'Tank internal assessment',
        'Boiler inspection',
        'Confined space evaluation'
      ],
      benefits: [
        'Access to confined spaces',
        'High-resolution imaging',
        'Real-time inspection',
        'Documentation capability'
      ],
      equipment: [
        'Borescopes and Fiberscopes',
        'Robotic Crawlers',
        'High-resolution Cameras',
        'Lighting Systems'
      ]
    },
    {
      id: 'oxide-scale',
      name: 'Oxide Scale Measurement',
      description: 'Specialized testing for measuring oxide scale thickness and composition in high-temperature applications.',
      applications: [
        'Boiler tube assessment',
        'Heat exchanger evaluation',
        'High-temperature component inspection',
        'Corrosion monitoring'
      ],
      benefits: [
        'Accurate scale measurement',
        'Non-destructive testing',
        'High-temperature capability',
        'Composition analysis'
      ],
      equipment: [
        'Ultrasonic Thickness Gauges',
        'Eddy Current Probes',
        'High-temperature Sensors',
        'Analysis Software'
      ]
    }
  ];

  const conventionalServices: ServiceDetail[] = [
    {
      id: 'ultrasonic-flaw',
      name: 'Ultrasonic Flaw Detection',
      description: 'Traditional ultrasonic testing for detecting internal defects and discontinuities in materials.',
      applications: [
        'Weld inspection',
        'Forging evaluation',
        'Castings assessment',
        'Plate and bar inspection'
      ],
      benefits: [
        'Deep penetration capability',
        'Accurate defect sizing',
        'Portable equipment',
        'Cost-effective method'
      ],
      equipment: [
        'Ultrasonic Flaw Detectors',
        'Contact and Immersion Probes',
        'Calibration Blocks',
        'Coupling Gels'
      ]
    },
    {
      id: 'thickness-gauging',
      name: 'Thickness Gauging',
      description: 'Measurement of material thickness using ultrasonic techniques for corrosion monitoring.',
      applications: [
        'Corrosion monitoring',
        'Thickness measurement',
        'Material verification',
        'Quality control'
      ],
      benefits: [
        'Accurate thickness measurement',
        'One-sided access',
        'Real-time results',
        'Data logging capability'
      ],
      equipment: [
        'Digital Thickness Gauges',
        'Dual-element Probes',
        'Calibration Standards',
        'Data Management Software'
      ]
    },
    {
      id: 'magnetic-particle',
      name: 'Magnetic Particle Testing',
      description: 'Surface and near-surface crack detection in ferromagnetic materials using magnetic fields.',
      applications: [
        'Weld inspection',
        'Forging evaluation',
        'Machined parts testing',
        'Structural component assessment'
      ],
      benefits: [
        'High sensitivity to surface cracks',
        'Fast inspection method',
        'Visual indication of defects',
        'Portable equipment'
      ],
      equipment: [
        'Magnetic Yokes',
        'Magnetic Particles',
        'UV Lamps',
        'Demagnetizers'
      ]
    },
    {
      id: 'liquid-penetrant',
      name: 'Liquid Penetrant Testing',
      description: 'Surface crack detection using capillary action of liquid penetrants on clean surfaces.',
      applications: [
        'Surface crack detection',
        'Weld inspection',
        'Machined parts testing',
        'Non-ferromagnetic materials'
      ],
      benefits: [
        'Works on all non-porous materials',
        'High sensitivity',
        'Visual indication',
        'Cost-effective method'
      ],
      equipment: [
        'Penetrant Materials',
        'Cleaners and Removers',
        'Developers',
        'UV Lamps'
      ]
    },
    {
      id: 'hardness-testing',
      name: 'On-Site Hardness Testing',
      description: 'Portable hardness measurement for material property verification and quality control.',
      applications: [
        'Material verification',
        'Heat treatment verification',
        'Quality control',
        'Failure analysis'
      ],
      benefits: [
        'Portable testing',
        'Non-destructive method',
        'Accurate measurements',
        'Real-time results'
      ],
      equipment: [
        'Portable Hardness Testers',
        'Indenters',
        'Calibration Standards',
        'Data Loggers'
      ]
    },
    {
      id: 'coating-thickness',
      name: 'Coating Thickness Measurement',
      description: 'Measurement of protective coating thickness using magnetic and eddy current methods.',
      applications: [
        'Paint thickness measurement',
        'Galvanizing verification',
        'Quality control',
        'Compliance testing'
      ],
      benefits: [
        'Non-destructive testing',
        'Accurate measurements',
        'Fast inspection',
        'Data logging'
      ],
      equipment: [
        'Coating Thickness Gauges',
        'Magnetic Probes',
        'Eddy Current Probes',
        'Calibration Foils'
      ]
    }
  ];

  const toggleService = (serviceId: string) => {
    setExpandedService(expandedService === serviceId ? null : serviceId);
  };

  const ServiceCard: React.FC<{ service: ServiceDetail; category: string }> = ({ service, category }) => {
    const isExpanded = expandedService === service.id;

    return (
      <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
        <button
          onClick={() => toggleService(service.id)}
          className="w-full px-6 py-4 text-left focus:outline-none focus:ring-2 focus:ring-avis-blue focus:ring-inset"
        >
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">{service.name}</h3>
            <svg
              className={`w-5 h-5 text-avis-blue transform transition-transform duration-200 ${
                isExpanded ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          <p className="mt-2 text-gray-600">{service.description}</p>
        </button>

        {isExpanded && (
          <div className="px-6 pb-6 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Applications</h4>
                <ul className="space-y-1">
                  {service.applications.map((app, index) => (
                    <li key={index} className="text-sm text-gray-600 flex items-start">
                      <span className="w-1.5 h-1.5 bg-avis-blue rounded-full mt-2 mr-2 flex-shrink-0"></span>
                      {app}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Benefits</h4>
                <ul className="space-y-1">
                  {service.benefits.map((benefit, index) => (
                    <li key={index} className="text-sm text-gray-600 flex items-start">
                      <span className="w-1.5 h-1.5 bg-avis-blue rounded-full mt-2 mr-2 flex-shrink-0"></span>
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Equipment</h4>
                <ul className="space-y-1">
                  {service.equipment.map((item, index) => (
                    <li key={index} className="text-sm text-gray-600 flex items-start">
                      <span className="w-1.5 h-1.5 bg-avis-blue rounded-full mt-2 mr-2 flex-shrink-0"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section className="bg-avis-blue text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl lg:text-5xl font-bold mb-4">
              Our NDT Services
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Comprehensive non-destructive testing solutions using both advanced 
              technologies and conventional methods to ensure quality and safety.
            </p>
          </div>
        </div>
      </section>

      {/* Advanced NDT Services */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Advanced NDT Services
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Cutting-edge non-destructive testing technologies for the most 
              demanding inspection requirements.
            </p>
          </div>

          <div className="space-y-4">
            {advancedServices.map((service) => (
              <ServiceCard key={service.id} service={service} category="Advanced NDT" />
            ))}
          </div>
        </div>
      </section>

      {/* Conventional NDT Services */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Conventional NDT Services
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Traditional and proven non-destructive testing methods for reliable 
              and cost-effective inspection solutions.
            </p>
          </div>

          <div className="space-y-4">
            {conventionalServices.map((service) => (
              <ServiceCard key={service.id} service={service} category="Conventional NDT" />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-avis-blue text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Need a Custom Solution?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Our expert team can develop tailored NDT solutions for your specific 
            requirements and industry needs.
          </p>
          <Link
            to="/quotation"
            className="bg-white text-avis-blue px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-50 transition-colors duration-200 inline-block"
          >
            Request a Quote
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
